import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Play,
  Pause,
  Radio,
  MessageCircle,
  Mic,
  Sparkles,
  QrCode,
  Heart,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Cell,
} from 'recharts';

// ---------- Data ----------
const KPIS = [
  { id: 'nps', label: 'Network NPS', value: '58', delta: '+6%', up: true },
  { id: 'footfall', label: 'Footfall · today', value: '14,820', delta: '+12.4%', up: true },
  { id: 'tr', label: 'Test-ride conv.', value: '49.3%', delta: '+3.1%', up: true },
  { id: 'book', label: 'Booking conv.', value: '14.8%', delta: '+0.4%', up: true },
];

const NPS_7D = [
  { d: 'Mon', v: 52 },
  { d: 'Tue', v: 53 },
  { d: 'Wed', v: 55 },
  { d: 'Thu', v: 54 },
  { d: 'Fri', v: 56 },
  { d: 'Sat', v: 57 },
  { d: 'Sun', v: 58 },
];

const FUNNEL = [
  { stage: 'Footfall', v: 1400 },
  { stage: 'Check-in', v: 1205 },
  { stage: 'Test Ride', v: 690 },
  { stage: 'Quote', v: 412 },
  { stage: 'Booking', v: 218 },
  { stage: 'Delivery', v: 182 },
];

const SENTIMENT_7D = [
  { d: 'Mon', pos: 62, neu: 24, neg: 14 },
  { d: 'Tue', pos: 58, neu: 28, neg: 14 },
  { d: 'Wed', pos: 65, neu: 22, neg: 13 },
  { d: 'Thu', pos: 70, neu: 20, neg: 10 },
  { d: 'Fri', pos: 68, neu: 22, neg: 10 },
  { d: 'Sat', pos: 72, neu: 19, neg: 9 },
  { d: 'Sun', pos: 74, neu: 18, neg: 8 },
];

const FEEDBACK_POOL = [
  {
    name: 'Aarav S.',
    city: 'Bengaluru — Indiranagar',
    when: '4m ago',
    text: '"Test ride was smooth, executive Rohit explained battery removal really well."',
    tags: ['test-ride', 'staff', 'battery'],
    nps: 9,
    channel: 'whatsapp',
    sent: 'pos',
  },
  {
    name: 'Diya P.',
    city: 'Mumbai — Bandra',
    when: '11m ago',
    text: '"Loved the scooter but waited 35 minutes for the test ride slot."',
    tags: ['wait-time', 'test-ride'],
    nps: 6,
    channel: 'qr',
    sent: 'neu',
  },
  {
    name: 'Rehan K.',
    city: 'Delhi — Karol Bagh',
    when: '18m ago',
    text: '"Sales person didn\'t share finance options. Felt pushed towards the top variant."',
    tags: ['finance', 'pushy-sales'],
    nps: 3,
    channel: 'voice',
    sent: 'neg',
  },
  {
    name: 'Ishita R.',
    city: 'Pune — Koregaon Park',
    when: '22m ago',
    text: '"Booking flow on the tablet was crisp. Got my OTP, done in 6 minutes."',
    tags: ['booking', 'speed'],
    nps: 10,
    channel: 'in-store',
    sent: 'pos',
  },
  {
    name: 'Karthik M.',
    city: 'Chennai — T. Nagar',
    when: '29m ago',
    text: '"AC was off, showroom felt hot. Battery demo unit was not charged."',
    tags: ['showroom', 'demo-unit'],
    nps: 4,
    channel: 'whatsapp',
    sent: 'neg',
  },
  {
    name: 'Sneha V.',
    city: 'Hyderabad — Jubilee Hills',
    when: '41m ago',
    text: '"Got a follow up call within 2 hours. They knew exactly which model I rode."',
    tags: ['follow-up', 'personalisation'],
    nps: 8,
    channel: 'voice',
    sent: 'pos',
  },
  {
    name: 'Aditya N.',
    city: 'Ahmedabad — Satellite',
    when: '55m ago',
    text: '"Liked VX2 Plus colors. Would love a longer test route."',
    tags: ['product', 'test-ride'],
    nps: 7,
    channel: 'qr',
    sent: 'pos',
  },
  {
    name: 'Pooja D.',
    city: 'Jaipur — Vaishali',
    when: '64m ago',
    text: '"Walked out — nobody approached me for 12 minutes."',
    tags: ['staffing', 'walkout'],
    nps: 2,
    channel: 'qr',
    sent: 'neg',
  },
];

const CUSTOMERS = [
  {
    id: 'PULSE-AARAV-001',
    name: 'Aarav S.',
    city: 'Bengaluru',
    tag: 'ice switcher · owner',
    color: 'amber',
    timeline: [
      { day: 'Day 0 · 11:42', channel: 'QR Scan', title: 'Scanned QR at door', body: 'Profiled as ICE-switcher · range + cost top concerns · 30-day timeline', sent: 'neu' },
      { day: 'Day 0 · 12:08', channel: 'In-store', title: 'Test ride · VX2 Plus', body: '8 km loop · battery removal demo · executive Rohit', sent: 'pos' },
      { day: 'Day 0 · 14:30', channel: 'WhatsApp', title: 'Post-visit nudge', body: "Personalised: range comparator vs his Activa 6G — '74% fuel savings/yr'", sent: 'pos' },
      { day: 'Day 1 · 10:15', channel: 'Voice AI', title: 'AI voice follow-up', body: 'Answered EMI in Hindi · routed to finance specialist', sent: 'pos' },
      { day: 'Day 3 · 16:50', channel: 'In-store', title: 'Booking confirmed', body: 'VX2 Plus · ₹3,499 EMI · trade-in initiated', sent: 'pos' },
      { day: 'Day 9 · 11:00', channel: 'Delivery', title: 'Delivery', body: 'RTO, insurance bundled · PULSE ID upgraded to OWNER', sent: 'pos' },
      { day: 'Day 32 · 09:20', channel: 'WhatsApp', title: 'First service reminder', body: 'Auto-routed to nearest charging point · slot pre-booked', sent: 'neu' },
      { day: 'Day 180 · —', channel: 'Referral', title: 'Owner-led test ride', body: 'Aarav invited 2 friends · 1 booked · ₹2k VIDA Edge credit earned', sent: 'pos' },
    ],
    nba: {
      title: 'Trigger referral campaign — 90-day owner mark',
      body: 'Aarav hit 90 days of ownership with zero complaints + 1 confirmed referral. ICE-switchers at this mark convert friends at 2.8× baseline.',
      via: 'via WhatsApp',
      impact: '+1.4 referred bookings / 30d',
      conf: 87,
    },
  },
  {
    id: 'PULSE-DIYA-002',
    name: 'Diya P.',
    city: 'Mumbai',
    tag: 'first time · prospect',
    color: 'cyan',
    timeline: [
      { day: 'Day 0 · 14:12', channel: 'QR Scan', title: 'Walked into Bandra showroom', body: 'First-time EV buyer · budget ₹1.4-1.6L · prefers Sport variant', sent: 'pos' },
      { day: 'Day 0 · 14:36', channel: 'In-store', title: 'Demo + spec walk', body: 'Spent 22 minutes · asked 3 range questions · 1 charging question', sent: 'pos' },
      { day: 'Day 0 · 14:58', channel: 'In-store', title: 'Wait for test ride', body: '35-minute wait flagged · NPS dip · executive overloaded', sent: 'neg' },
      { day: 'Day 0 · 16:10', channel: 'WhatsApp', title: 'Apology + slot booked', body: 'Auto-apology + Day-2 priority slot offered · accepted', sent: 'neu' },
      { day: 'Day 2 · 11:00', channel: 'In-store', title: 'Test ride · VX2', body: '6 km loop · liked acceleration · pricing concern surfaced', sent: 'pos' },
      { day: 'Day 2 · 13:45', channel: 'Voice AI', title: 'EMI options in Marathi', body: 'AI ran 3 EMI scenarios · shared finance partner shortlist', sent: 'pos' },
      { day: 'Day 5 · 18:00', channel: 'WhatsApp', title: 'Brochure + price-lock', body: '7-day price lock offered · accepted · still in consideration', sent: 'neu' },
      { day: 'Day 7 · —', channel: 'Pending', title: 'Quote follow-up due', body: 'Outreach window closes Day 9 — high-intent prospect, EMI cleared', sent: 'neu' },
    ],
    nba: {
      title: 'Convert in next 48h — price-lock expiry nudge',
      body: 'Diya cleared EMI, no objections remaining. First-timers with cleared EMI convert 3.1× when nudged within 48h of price-lock expiry.',
      via: 'via WhatsApp + voice',
      impact: '+1 booking · ₹1.4L revenue',
      conf: 91,
    },
  },
  {
    id: 'PULSE-REHAN-003',
    name: 'Rehan K.',
    city: 'Delhi',
    tag: 'browsing · lost',
    color: 'zinc',
    timeline: [
      { day: 'Day 0 · 13:05', channel: 'QR Scan', title: 'Karol Bagh walk-in', body: 'Browsing · 6-12 month timeline · price-sensitive', sent: 'neu' },
      { day: 'Day 0 · 13:18', channel: 'In-store', title: 'Quote shared', body: 'Executive pushed top variant · finance options not shared', sent: 'neg' },
      { day: 'Day 0 · 13:32', channel: 'In-store', title: 'Walked out', body: 'Exit kiosk: "felt pushed" · "wanted base variant info"', sent: 'neg' },
      { day: 'Day 0 · 16:00', channel: 'WhatsApp', title: 'Auto recovery DM', body: 'Apology + base variant brochure + no-pressure script', sent: 'neu' },
      { day: 'Day 1 · 10:30', channel: 'Voice AI', title: 'AI re-engagement', body: 'Hindi conversation · no upsell · simple range + cost answers', sent: 'pos' },
      { day: 'Day 3 · 11:15', channel: 'WhatsApp', title: 'Owner referral video', body: 'Sent organic owner content · no CTA · trust rebuild', sent: 'pos' },
      { day: 'Day 14 · —', channel: 'Pending', title: 'Cooling-off complete', body: 'Re-warm with neutral content cadence — no quote re-push until Day 30', sent: 'neu' },
      { day: 'Day 30 · —', channel: 'Pending', title: 'Re-engagement window', body: 'Recovered lead window opens · estimated 18% conversion if executive script v2 is used', sent: 'neu' },
    ],
    nba: {
      title: 'Flag executive · retrain on base-variant pitch',
      body: 'Rehan is one of 14 walkouts this week from Karol Bagh tagged "pushy-sales". Pattern detected: same executive in 9 of 14 cases. Coaching nudge recommended.',
      via: 'via dealer dashboard',
      impact: 'Reduce walkout rate ~3 pp',
      conf: 82,
    },
  },
];

const TOUR_STEPS = [
  {
    n: '01',
    layer: 'Gamified QR Profile',
    title: '60 seconds. A profile for life.',
    body:
      'Five quick taps — buyer type, concerns, timeline. The customer is profiled before they even take a test ride. Every subsequent interaction (WhatsApp, voice, service, repurchase) auto-personalises to this rider.',
    bullets: [
      'Branches content for First-time, ICE-switcher, EV-upgrader',
      'Concerns drive the WhatsApp + voice scripts',
      'Same PULSE ID covers ownership, service, referral',
      'Profile grows richer at every touchpoint — not just at QR',
    ],
    icon: QrCode,
  },
  {
    n: '02',
    layer: 'WhatsApp Feedback Bot',
    title: 'Honest answers, in their language.',
    body:
      'Two hours after a customer leaves the showroom, an AI bot DMs them on WhatsApp in their preferred language. Conversational, 30 seconds, NPS + open feedback — they talk to AI, not to the salesperson they\'re rating.',
    bullets: [
      'Vernacular: Hindi + 9 Indian languages',
      'NPS captured in 30 seconds, free-text in 60',
      '58% response rate (vs ~4% for SMS surveys)',
      'Auto-tagged + routed to the right HQ team',
    ],
    icon: MessageCircle,
  },
  {
    n: '03',
    layer: 'Voice AI Follow-up',
    title: 'Hot leads, never cold.',
    body:
      'When a prospect shows high intent but doesn\'t book in 24h, an AI voice agent calls them in their language. Answers EMI, range, charging and trade-in questions. Hands warm leads to the dealership only when ready.',
    bullets: [
      'Conversational AI · pause, interrupt, code-switch',
      'Triggers from intent score, not from a manual list',
      '2.4× re-engagement on otherwise-cold leads',
      'Logged conversations land in the customer\'s PULSE ID',
    ],
    icon: Mic,
  },
  {
    n: '04',
    layer: 'Sentiment & Theme AI',
    title: 'Themes surface in seconds.',
    body:
      'Every piece of text and voice is auto-tagged across 50+ themes — wait-time, finance, demo-bike, range, after-sales — and sentiment-scored per dealership, per city, per executive. No analyst reads 50,000 lines.',
    bullets: [
      '50+ pre-trained themes for the 2W EV journey',
      'Dealership · city · executive · variant slicing',
      'Anomaly detection — bad batch, training gap, repeat complaint',
      'Reports to HQ Slack/Email when a theme spikes',
    ],
    icon: Sparkles,
  },
  {
    n: '05',
    layer: 'Live HQ Pulse Dashboard',
    title: 'One screen for the whole network.',
    body:
      'NPS by city, funnel drop-offs, hot complaints, executive performance — refreshing every minute. The CEO checks this screen before coffee. The COO acts on it before lunch.',
    bullets: [
      'Drilldown: network → city → dealership → executive',
      'Live alerts for spike themes or NPS drops',
      'Owner-cohort views (90-day, 180-day, repurchase)',
      'Exports to board decks and supply-chain triggers',
    ],
    icon: Radio,
  },
];

// ---------- Atoms ----------
const Pill = ({ children, tone = 'zinc' }) => {
  const map = {
    amber: 'border-amber/40 text-amber',
    cyan: 'border-cyan-300/40 text-cyan-300',
    pos: 'border-emerald-400/40 text-emerald-300',
    neu: 'border-zinc-500/40 text-zinc-300',
    neg: 'border-rose-400/40 text-rose-300',
    zinc: 'border-white/15 text-zinc-300',
  };
  return (
    <span
      className={`inline-flex items-center font-mono text-[9px] uppercase tracking-[0.18em] border px-2 py-0.5 ${map[tone] || map.zinc}`}
    >
      {children}
    </span>
  );
};

const KickerLabel = ({ children }) => (
  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber mb-3">
    {children}
  </div>
);

const SectionHeader = ({ kicker, title }) => (
  <div>
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-px bg-amber" />
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber">
        {kicker}
      </span>
    </div>
    <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-balance text-white leading-[1.05]">
      {title}
    </h2>
  </div>
);

// ---------- HQ Console ----------
const HQConsole = () => {
  const [tf, setTf] = useState('today');
  const [clock, setClock] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const time = clock.toLocaleTimeString('en-IN', { hour12: false });

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <KickerLabel>HQ Console</KickerLabel>
          <h1 className="font-display text-[40px] sm:text-5xl md:text-6xl lg:text-7xl text-balance max-w-4xl leading-[1.02]">
            The pulse of <span className="text-amber">750+ floors.</span>
            <sup className="text-amber text-xl ml-1">*</sup>
          </h1>
          <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500">
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
              Last sync: {time} · auto-refreshing
            </span>
          </div>
        </div>
        <div className="flex border border-white/15">
          {['today', 'week', 'month'].map((t) => (
            <button
              key={t}
              onClick={() => setTf(t)}
              data-testid={`tf-${t}`}
              className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
                tf === t ? 'bg-amber text-black' : 'text-zinc-300 hover:bg-white/5'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* KPI tiles */}
      <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/10">
        {KPIS.map((k) => (
          <div key={k.id} className="bg-ink-900 p-5 md:p-6">
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
              {k.label}
            </div>
            <div className="mt-2 flex items-baseline gap-3">
              <span className="font-display text-4xl md:text-5xl text-amber tabular-nums">
                {k.value}
              </span>
              <span className={`inline-flex items-center gap-1 font-mono text-xs ${k.up ? 'text-emerald-300' : 'text-rose-300'}`}>
                {k.up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {k.delta}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- NPS sparkline + Funnel + Capture ----------
const NPSSpark = () => (
  <div className="bg-ink-900 border border-white/10 p-5 md:p-6">
    <div className="flex justify-between items-baseline">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
        Network NPS · 7d
      </div>
      <span className="font-mono text-[10px] text-emerald-300">+6 vs LW</span>
    </div>
    <div className="mt-2 font-display text-4xl text-amber tabular-nums">58</div>
    <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.18em]">
      Promoters − Detractors
    </div>
    <div className="h-20 mt-3">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={NPS_7D} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <Line type="monotone" dataKey="v" stroke="#F59E0B" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const FunnelCard = () => (
  <div className="bg-ink-900 border border-white/10 p-5 md:p-6 lg:col-span-2">
    <div className="flex justify-between items-baseline mb-1">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
          Customer Journey Funnel
        </div>
        <div className="text-sm text-zinc-400 mt-1">
          From walk-in to delivery — last 7 days
        </div>
      </div>
      <span className="font-mono text-[10px] text-cyan-300">13.0% end-to-end</span>
    </div>
    <div className="h-44 mt-2">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={FUNNEL} margin={{ top: 8, right: 0, bottom: 4, left: -16 }}>
          <XAxis dataKey="stage" tick={{ fill: '#a1a1aa', fontSize: 10, fontFamily: 'monospace' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fill: '#52525b', fontSize: 10, fontFamily: 'monospace' }} axisLine={false} tickLine={false} />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.04)' }}
            contentStyle={{ background: '#0f0f12', border: '1px solid #27272a', borderRadius: 0, fontSize: 12 }}
          />
          <Bar dataKey="v">
            {FUNNEL.map((_, i) => (
              <Cell key={i} fill={i === 0 ? '#F59E0B' : i === FUNNEL.length - 1 ? '#06B6D4' : '#a16207'} fillOpacity={i === 0 || i === FUNNEL.length - 1 ? 1 : 0.85} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const CaptureRateCard = () => (
  <div className="bg-ink-900 border border-amber/40 p-5 md:p-6">
    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber">
      Capture Rate
    </div>
    <div className="mt-2 font-display text-5xl md:text-6xl text-white tabular-nums">86.2%</div>
    <div className="text-sm text-zinc-400 mt-2 leading-snug">
      of walk-ins now logged via QR + WhatsApp
    </div>
    <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-300">
      Median response time 7 min
    </div>
  </div>
);

// ---------- Sentiment bar ----------
const SentimentChart = () => (
  <div className="bg-ink-900 border border-white/10 p-5 md:p-6">
    <div className="flex justify-between items-baseline">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
          Sentiment Pulse · 7d
        </div>
        <div className="text-sm text-zinc-400 mt-1">
          AI-tagged feedback across 750+ floors
        </div>
      </div>
      <div className="flex gap-3 font-mono text-[10px] uppercase tracking-[0.18em]">
        <span className="text-emerald-300">● Positive</span>
        <span className="text-zinc-400">● Neutral</span>
        <span className="text-rose-300">● Negative</span>
      </div>
    </div>
    <div className="h-60 mt-4">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={SENTIMENT_7D} margin={{ top: 8, right: 0, bottom: 4, left: -16 }} stackOffset="expand">
          <XAxis dataKey="d" tick={{ fill: '#a1a1aa', fontSize: 10, fontFamily: 'monospace' }} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={(v) => `${Math.round(v * 100)}`} tick={{ fill: '#52525b', fontSize: 10, fontFamily: 'monospace' }} axisLine={false} tickLine={false} />
          <Tooltip cursor={{ fill: 'rgba(255,255,255,0.04)' }} contentStyle={{ background: '#0f0f12', border: '1px solid #27272a', borderRadius: 0, fontSize: 12 }} />
          <Bar dataKey="pos" stackId="a" fill="#34D399" />
          <Bar dataKey="neu" stackId="a" fill="#71717a" />
          <Bar dataKey="neg" stackId="a" fill="#FB7185" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
);

// ---------- Live feedback stream ----------
const ChannelDot = ({ ch }) => {
  const map = {
    whatsapp: 'bg-emerald-400',
    qr: 'bg-amber',
    voice: 'bg-cyan-300',
    'in-store': 'bg-violet-400',
  };
  return (
    <span className={`inline-block w-1.5 h-1.5 rounded-full ${map[ch] || 'bg-zinc-400'}`} />
  );
};

const FeedbackStream = () => {
  const [items, setItems] = useState(FEEDBACK_POOL);

  // simulate stream — rotate every 4s
  useEffect(() => {
    const id = setInterval(() => {
      setItems((prev) => {
        const next = [...prev];
        next.unshift(next.pop());
        return next;
      });
    }, 4000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="bg-ink-900 border border-white/10 p-5 md:p-6">
      <div className="flex justify-between items-baseline mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
            Live Feedback Stream
          </div>
          <div className="text-sm text-zinc-400 mt-1">AI-classified · realtime</div>
        </div>
        <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-300">
          <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
          streaming
        </span>
      </div>
      <div className="space-y-3 max-h-[560px] overflow-y-auto pr-2">
        {items.map((f, i) => (
          <div
            key={f.name + i}
            className={`border-l-2 ${
              f.sent === 'pos' ? 'border-emerald-400/60' : f.sent === 'neg' ? 'border-rose-400/60' : 'border-zinc-500/60'
            } bg-black/30 p-4 transition-all`}
          >
            <div className="flex items-baseline justify-between gap-3">
              <div className="font-medium text-white text-sm">{f.name}</div>
              <div className="font-mono text-[10px] text-zinc-500">{f.when}</div>
            </div>
            <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
              {f.city}
            </div>
            <p className="mt-2 text-sm text-zinc-200 italic leading-relaxed">{f.text}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2 justify-between">
              <div className="flex flex-wrap gap-1.5">
                {f.tags.map((t) => (
                  <span key={t} className="font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-400 bg-white/5 px-2 py-0.5">
                    #{t}
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.18em]">
                <span className={`tabular-nums ${
                  f.nps >= 9 ? 'text-emerald-300' : f.nps >= 7 ? 'text-amber' : f.nps >= 5 ? 'text-zinc-300' : 'text-rose-300'
                }`}>
                  NPS {f.nps}
                </span>
                <span className="inline-flex items-center gap-1.5 text-zinc-400">
                  <ChannelDot ch={f.channel} />
                  {f.channel}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Day-in-the-life replay ----------
const JourneyReplay = () => {
  const [cid, setCid] = useState(CUSTOMERS[0].id);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const cust = useMemo(() => CUSTOMERS.find((c) => c.id === cid), [cid]);
  const total = cust.timeline.length;
  const timerRef = useRef(null);

  useEffect(() => {
    if (playing) {
      timerRef.current = setInterval(() => {
        setStep((s) => {
          if (s + 1 >= total) {
            setPlaying(false);
            return total - 1;
          }
          return s + 1;
        });
      }, 1300);
    }
    return () => clearInterval(timerRef.current);
  }, [playing, total]);

  // reset when customer changes
  useEffect(() => {
    setStep(0);
    setPlaying(false);
  }, [cid]);

  const sentTone = (s) => (s === 'pos' ? 'pos' : s === 'neg' ? 'neg' : 'neu');

  return (
    <div>
      <SectionHeader
        kicker="Day-in-the-life replay"
        title={
          <>
            One PULSE ID. <span className="text-amber">Every touch.</span>
          </>
        }
      />
      <p className="text-zinc-400 max-w-3xl mt-4">
        Pick a customer. Watch every interaction VIDA had with them — from a QR scan to ownership — in one cinematic timeline.
      </p>

      <div className="mt-10 grid lg:grid-cols-3 gap-px bg-white/5 border border-white/10">
        {CUSTOMERS.map((c) => (
          <button
            key={c.id}
            onClick={() => setCid(c.id)}
            data-testid={`cust-${c.id}`}
            className={`text-left bg-ink-900 p-5 md:p-6 transition-colors ${
              cid === c.id ? 'ring-2 ring-amber ring-inset' : 'hover:bg-white/5'
            }`}
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              {c.id}
            </div>
            <div className="mt-2 font-display text-xl text-white">
              {c.name} · <span className="text-zinc-400">{c.city}</span>
            </div>
            <div className="mt-2">
              <Pill tone={c.color === 'amber' ? 'amber' : c.color === 'cyan' ? 'cyan' : 'zinc'}>
                {c.tag}
              </Pill>
            </div>
          </button>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-[1.4fr,1fr] gap-6">
        {/* Timeline */}
        <div className="bg-ink-900 border border-white/10 p-5 md:p-6">
          <div className="flex justify-between items-center mb-4">
            <button
              onClick={() => setPlaying((p) => !p)}
              data-testid="play-journey"
              className="inline-flex items-center gap-2 bg-amber text-black px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-amber/90"
            >
              {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
              {playing ? 'Pause' : 'Play journey'}
            </button>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              {step + 1} / {total}
            </div>
          </div>

          {/* progress bar */}
          <div className="h-1 bg-white/5 mb-6">
            <div
              className="h-full bg-amber transition-all duration-500"
              style={{ width: `${((step + 1) / total) * 100}%` }}
            />
          </div>

          <ol className="space-y-3">
            {cust.timeline.map((t, i) => {
              const active = i <= step;
              return (
                <li
                  key={i}
                  onClick={() => setStep(i)}
                  className={`cursor-pointer border-l-2 pl-4 py-2 transition-all ${
                    i === step
                      ? 'border-amber bg-amber/5'
                      : active
                      ? 'border-amber/40'
                      : 'border-white/10 opacity-40'
                  }`}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                      {t.day}
                    </span>
                    <Pill tone={sentTone(t.sent)}>{t.channel}</Pill>
                  </div>
                  <div className="mt-1 font-display text-base text-white">{t.title}</div>
                  <div className="mt-1 text-sm text-zinc-400 leading-snug">{t.body}</div>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Next Best Action */}
        <div className="bg-ink-900 border border-amber/40 p-5 md:p-6 self-start">
          <div className="flex justify-between items-baseline">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber">
              Next Best Action
            </div>
            <span className="font-mono text-[10px] text-cyan-300">
              AI · {cust.nba.conf}% conf
            </span>
          </div>
          <div className="mt-3 font-display text-xl text-white leading-snug">
            {cust.nba.title}
          </div>
          <p className="mt-3 text-sm text-zinc-300 leading-relaxed">{cust.nba.body}</p>
          <div className="mt-4 border-t border-white/10 pt-4 space-y-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              {cust.nba.via}
            </div>
            <div className="font-mono text-[11px] text-amber">{cust.nba.impact}</div>
          </div>
          <button
            data-testid="trigger-nba"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-amber text-black px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-amber/90"
          >
            Trigger now
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Tour carousel ----------
const TourCarousel = () => {
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const total = TOUR_STEPS.length;
  const t = TOUR_STEPS[step];
  const Icon = t.icon;
  const timerRef = useRef(null);

  useEffect(() => {
    if (playing) {
      timerRef.current = setInterval(() => {
        setStep((s) => (s + 1) % total);
      }, 4500);
    }
    return () => clearInterval(timerRef.current);
  }, [playing, total]);

  return (
    <div>
      <SectionHeader
        kicker="Auto-play tour · for leadership"
        title={
          <>
            Welcome to VIDA.{' '}
            <span className="text-amber">We&apos;ll remember you for life.</span>
          </>
        }
      />
      <p className="text-zinc-400 max-w-3xl mt-4">
        One scan · 60 seconds · zero app. From your first test ride to your
        first service to your next bike — one PULSE ID, always you.
      </p>

      <div className="mt-10 flex flex-wrap items-center gap-3">
        <button
          onClick={() => setPlaying((p) => !p)}
          data-testid="play-tour"
          className="inline-flex items-center gap-2 bg-amber text-black px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-amber/90"
        >
          {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
          {playing ? 'Pause tour' : 'Start tour'}
        </button>
        <div className="flex gap-1">
          {TOUR_STEPS.map((s, i) => (
            <button
              key={s.n}
              onClick={() => setStep(i)}
              className={`px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] border ${
                i === step ? 'bg-amber text-black border-amber' : 'border-white/15 text-zinc-400 hover:border-amber/50'
              }`}
            >
              {s.n}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 bg-ink-900 border border-white/10 p-6 md:p-10 grid lg:grid-cols-[120px,1fr] gap-6 lg:gap-10">
        <div className="flex lg:flex-col items-start gap-4">
          <div className="grid h-14 w-14 place-items-center border border-amber/40 bg-amber/10">
            <Icon className="w-6 h-6 text-amber" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              Layer {t.n}
            </div>
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber mt-1">
              {t.layer}
            </div>
          </div>
        </div>
        <div>
          <h3 className="font-display text-2xl md:text-4xl text-white leading-snug">
            {t.title}
          </h3>
          <p className="mt-4 text-base text-zinc-300 leading-relaxed max-w-3xl">
            {t.body}
          </p>
          <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-2">
            {t.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-zinc-300">
                <span className="text-amber mt-1.5 w-1 h-1 rounded-full bg-amber inline-block flex-shrink-0" />
                {b}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* progress */}
      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1 h-1 bg-white/5">
          <div
            className="h-full bg-amber transition-all duration-500"
            style={{ width: `${((step + 1) / total) * 100}%` }}
          />
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 tabular-nums">
          {step + 1} / {total}
        </span>
      </div>
    </div>
  );
};

// ---------- Meta ----------
const DEMO_META = {
  title: 'PULSE OS · Live Demo — HQ console, journey replay & layer tour',
  description:
    'A live concept demo of PULSE OS for VIDA — HQ dashboard, customer journey replay, AI feedback stream, and 5-layer interactive tour.',
  image: 'https://themobilitygroup.co/og-vida.png',
  url: 'https://themobilitygroup.co/vidaproject/demo',
};

const useDemoMeta = () => {
  useEffect(() => {
    const originalTitle = document.title;
    const originals = {};
    const setMeta = (sel, v) => {
      const el = document.querySelector(sel);
      if (el) {
        originals[sel] = el.getAttribute('content');
        el.setAttribute('content', v);
      }
    };
    document.title = DEMO_META.title;
    setMeta('meta[property="og:title"]', DEMO_META.title);
    setMeta('meta[property="og:description"]', DEMO_META.description);
    setMeta('meta[property="og:image"]', DEMO_META.image);
    setMeta('meta[property="og:url"]', DEMO_META.url);
    setMeta('meta[name="twitter:title"]', DEMO_META.title);
    setMeta('meta[name="twitter:description"]', DEMO_META.description);
    setMeta('meta[name="twitter:image"]', DEMO_META.image);
    setMeta('meta[name="description"]', DEMO_META.description);
    return () => {
      document.title = originalTitle;
      Object.entries(originals).forEach(([sel, v]) => {
        const el = document.querySelector(sel);
        if (el && v !== null) el.setAttribute('content', v);
      });
    };
  }, []);
};

// ---------- Page ----------
const VidaDemo = () => {
  useDemoMeta();
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Top bar */}
      <div className="sticky top-0 z-40 border-b border-white/10 bg-black/80 backdrop-blur-xl">
        <div className="px-6 md:px-12 lg:px-16 h-14 flex items-center justify-between">
          <Link
            to="/vidaproject"
            data-testid="demo-back-to-pitch"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-400 hover:text-amber"
          >
            <ArrowLeft className="w-3 h-3" />
            Back to pitch
          </Link>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              <Heart className="w-3 h-3 text-amber" />
              PULSE OS · Live Demo
            </span>
            <Link
              to="/contact"
              data-testid="demo-contact"
              className="inline-flex items-center gap-2 bg-amber text-black px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-amber/90"
            >
              Bring us a brief
            </Link>
          </div>
        </div>
      </div>

      {/* HQ Console */}
      <section className="px-6 md:px-12 lg:px-16 pt-14 md:pt-20 pb-12 md:pb-16 border-b border-white/10">
        <HQConsole />
      </section>

      {/* Charts row */}
      <section className="px-6 md:px-12 lg:px-16 py-12 md:py-16 border-b border-white/10">
        <div className="grid lg:grid-cols-4 gap-6">
          <NPSSpark />
          <FunnelCard />
          <CaptureRateCard />
        </div>
        <div className="mt-6 grid lg:grid-cols-[1.4fr,1fr] gap-6">
          <SentimentChart />
          <FeedbackStream />
        </div>
      </section>

      {/* Journey replay */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24 border-b border-white/10 bg-ink-900/40">
        <JourneyReplay />
      </section>

      {/* Tour */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24 border-b border-white/10">
        <TourCarousel />
      </section>

      {/* Footer disclaimer */}
      <section className="px-6 md:px-12 lg:px-16 py-12 md:py-16">
        <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-zinc-500 leading-relaxed">
          PULSE OS · concept demo · data shown is illustrative ·{' '}
          <span className="text-zinc-600">
            *Dealership count as provided by VIDA / Hero MotoCorp
          </span>
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <Link
            to="/vidaproject"
            className="inline-flex items-center gap-2 border border-white/20 text-white px-5 py-3 font-mono text-[10px] uppercase tracking-[0.18em] hover:border-amber hover:text-amber"
          >
            <ArrowLeft className="w-3 h-3" /> Back to the pitch
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-amber text-black px-5 py-3 font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-amber/90"
          >
            Bring us a brief
          </Link>
        </div>
      </section>
    </div>
  );
};

export default VidaDemo;
